import { useEffect, useState, useCallback } from "react";
import { trpc } from "@utils/trpc";
import { Platform, Linking } from "react-native";
import { captureException } from "@utils/captureException";
import { toast } from "sonner-native";

const EXTERNAL_LINK_ERROR_MESSAGE = "Could not open external link.";

const getExternalLinkErrorExtra = ({
  extLinks,
  urlToOpen,
  validatedExternalUrl,
  canOpenUrl,
}: {
  extLinks: string[];
  urlToOpen?: string;
  validatedExternalUrl: string | null;
  canOpenUrl?: boolean;
}) => ({
  canOpenUrl,
  extLinks,
  urlToOpen,
  validatedExternalUrl,
});

const useValidatedExternalUrl = (
  extLinks: string[],
  { accessId }: { accessId: string },
) => {
  // Validate external URLs via backend API (avoids CORS on web)
  const validateUrlsMutation = trpc.accesses.validateExternalUrls.useMutation();
  const [validatedExternalUrl, setValidatedExternalUrl] = useState<
    string | null
  >(null);

  const showExternalLinkError = useCallback(() => {
    toast.error(EXTERNAL_LINK_ERROR_MESSAGE);
  }, []);

  useEffect(() => {
    if (extLinks && extLinks.length > 0) {
      validateUrlsMutation
        .mutateAsync({ urls: extLinks })
        .then(({ validUrl }) => {
          if (validUrl) {
            console.log(`✅ Pre-validated URL: ${validUrl}`);
            setValidatedExternalUrl(validUrl);
          } else {
            console.log(
              "⚠️ No valid URLs found, will fall back to first URL on click",
            );
            setValidatedExternalUrl(extLinks[0]);
          }
        })
        .catch((error) => {
          console.error("❌ Error validating URLs:", error);
          // Fall back to first URL on error
          setValidatedExternalUrl(extLinks[0]);
        });
    }
  }, [extLinks]);

  const openExternalUrl = useCallback(async () => {
    const urlToOpen = validatedExternalUrl || extLinks[0];
    const fingerprint = [
      "open-validated-external-url",
      accessId || urlToOpen || "missing-url",
    ];
    const captureExtra = (canOpenUrl?: boolean) =>
      getExternalLinkErrorExtra({
        canOpenUrl,
        extLinks,
        urlToOpen,
        validatedExternalUrl,
      });

    if (!urlToOpen) {
      showExternalLinkError();
      captureException(new Error("No external URL available"), {
        channel: "error",
        area: "linking",
        operation: "open-validated-external-url",
        fingerprint,
        tags: {
          accessId,
        },
        extra: captureExtra(),
      });
      return;
    }

    if (Platform.OS === "web" && typeof window !== "undefined") {
      // Open in new tab for web platform
      window.open(urlToOpen, "_blank", "noopener,noreferrer");
    } else {
      // Use Linking for mobile platforms
      const canOpenUrl = await Linking.canOpenURL(urlToOpen);

      if (!canOpenUrl) {
        showExternalLinkError();
        captureException(new Error(`Cannot open external URL: ${urlToOpen}`), {
          channel: "error",
          area: "linking",
          operation: "open-validated-external-url",
          fingerprint,
          tags: {
            accessId,
          },
          extra: captureExtra(canOpenUrl),
        });
        return;
      }

      Linking.openURL(urlToOpen).catch((error) => {
        showExternalLinkError();
        captureException(error, {
          channel: "error",
          area: "linking",
          operation: "open-validated-external-url",
          fingerprint,
          tags: {
            accessId,
          },
          extra: captureExtra(canOpenUrl),
        });
      });
    }
  }, [accessId, extLinks, showExternalLinkError, validatedExternalUrl]);

  return openExternalUrl;
};

export default useValidatedExternalUrl;
